import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

export function apiResponseHandler<TGet, TPost = TGet>(
    apiResponse: ApiResponse<TGet> | ApiErrorResponse<TPost extends TGet ? TGet : TPost> | Error | null,
    onSuccess?: (successResponse: ApiResponse<TGet>) => void,
    onError?: (errorResponse: ApiErrorResponse<TPost extends TGet ? TGet : TPost>) => void
) {
    // Handle null/undefined responses
    if (!apiResponse) {
        const nullErrorResponse = {
            isError: true,
            data: null,
            status: 0,
            message: 'No response received'
        } as ApiErrorResponse<TPost extends TGet ? TGet : TPost>;
        return onError ? onError(nullErrorResponse) : apiResponse;
    }

    // Handle JavaScript Error objects (network errors, etc.) - Safari compatible
    if (
        apiResponse instanceof Error ||
        (typeof apiResponse === 'object' && apiResponse.constructor?.name === 'Error') ||
        (typeof apiResponse === 'object' && 'message' in apiResponse && 'stack' in apiResponse)
    ) {
        const errorResponse = {
            isError: true,
            data: null,
            status: 0,
            message: (apiResponse as Error).message || 'Network error'
        } as ApiErrorResponse<TPost extends TGet ? TGet : TPost>;
        return onError ? onError(errorResponse) : apiResponse;
    }

    // Handle structured API error responses - Safari compatible
    if (
        typeof apiResponse === 'object' &&
        (('isError' in apiResponse && apiResponse.isError === true) ||
            ('status' in apiResponse && typeof apiResponse.status === 'number' && apiResponse.status >= 400) ||
            ('data' in apiResponse &&
                typeof apiResponse.data === 'object' &&
                apiResponse.data !== null &&
                'isError' in apiResponse.data &&
                apiResponse.data.isError === true))
    ) {
        return onError ? onError(apiResponse as ApiErrorResponse<TPost extends TGet ? TGet : TPost>) : apiResponse;
    }

    // Handle success responses
    return onSuccess ? onSuccess(apiResponse as ApiResponse<TGet>) : apiResponse;
}

/**
 * Best-effort extraction of a human-readable message from a failed API response, so the UI can show
 * the server's actual reason (e.g. `403 {"error": "No active tenant for the author; …"}`) instead of
 * only a generic "something went wrong". Handles the fetch-helper's rejected shape
 * (`{isError, status, data: {…body}}`), plain problem-details bodies (`detail`/`title`) and JS Errors.
 * Returns undefined when no message can be found — callers fall back to their generic text.
 */
export function extractApiErrorMessage(apiResponse: unknown): string | undefined {
    if (!apiResponse || typeof apiResponse !== 'object') {
        return undefined;
    }

    if (apiResponse instanceof Error) {
        return apiResponse.message || undefined;
    }

    const body =
        'data' in apiResponse && typeof apiResponse.data === 'object' && apiResponse.data !== null
            ? (apiResponse.data as Record<string, unknown>)
            : (apiResponse as Record<string, unknown>);

    for (const key of ['error', 'detail', 'message', 'title']) {
        const value = body[key];
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
    }

    return undefined;
}

/**
 * Best-effort extraction of the HTTP status from a failed API response — the fetch-helper rejects
 * with `{isError, status, …}`. Lets callers distinguish a permanent client error (e.g. a 403) from a
 * transient server error (5xx) so a React Query retry policy only retries the recoverable ones.
 * Returns undefined for network errors / thrown JS Errors that never got a status.
 */
export function apiErrorStatus(apiResponse: unknown): number | undefined {
    if (!apiResponse || typeof apiResponse !== 'object') {
        return undefined;
    }

    const status = (apiResponse as { status?: unknown }).status;

    return typeof status === 'number' ? status : undefined;
}

export function apiResponseHandlerNew<TGet extends Record<string, unknown>, TPost = TGet>(
    apiResponse: ApiResponse<unknown, TGet> | ApiErrorResponse<TPost extends TGet ? TGet : TPost> | Error | null,
    onSuccess?: (successResponse: ApiResponse<unknown, TGet>) => void,
    onError?: (errorResponse: ApiErrorResponse<TPost extends TGet ? TGet : TPost> | Error | null) => void
) {
    // Handle null/undefined responses
    if (!apiResponse) {
        return onError ? onError(null) : apiResponse;
    }

    // Handle JavaScript Error objects (network errors, etc.) - Safari compatible
    if (
        apiResponse instanceof Error ||
        (typeof apiResponse === 'object' && apiResponse.constructor?.name === 'Error') ||
        (typeof apiResponse === 'object' && 'message' in apiResponse && 'stack' in apiResponse)
    ) {
        return onError ? onError(apiResponse as Error) : apiResponse;
    }

    // Handle structured API error responses - Safari compatible
    if (
        typeof apiResponse === 'object' &&
        (('isError' in apiResponse && apiResponse.isError === true) ||
            ('status' in apiResponse && typeof apiResponse.status === 'number' && apiResponse.status >= 400) ||
            ('data' in apiResponse &&
                typeof apiResponse.data === 'object' &&
                apiResponse.data !== null &&
                'isError' in apiResponse.data &&
                apiResponse.data.isError === true))
    ) {
        return onError ? onError(apiResponse as ApiErrorResponse<TPost extends TGet ? TGet : TPost>) : apiResponse;
    }

    // Handle success responses
    return onSuccess ? onSuccess(apiResponse as ApiResponse<unknown, TGet>) : apiResponse;
}
